import { Links, Meta, MetaFunction, Outlet, Scripts, useLocation, useNavigate } from 'react-router'
import type { LinksFunction } from 'react-router'
import RootStore, { __store } from './stores/RootStore'
import { useContext, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/remix'
import { setRouteSpan } from './lib/sentryTrace'
import { Box, CssBaseline } from '@mui/material'
import { ActionMenuForUIStore } from './components/ActionMenu'
import { BottomMessageDialog, UiStoreMessageDialog } from './components/MessageDialog'
import { I18nContext } from '../shared-lib/i18n/i18n'
import DropDownMessage from './components/DropDownMessage'
import './global.css'
import { observer } from 'mobx-react-lite'
import DevDisplay from './components/DevDisplay'
import ImageModal from './components/ImageModal'
import RefreshIcon from './components/RefreshIcon'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import LoadingSplash from './components/LoadingSplash'
import { configure, runInAction } from 'mobx'
import SearchModal from './components/SearchModal'
import SetupModal from './components/SetupModal'
import { useGlobalShortcuts } from './hooks/useGlobalShortcuts'
import { useCheckScreenSize } from './hooks/useCheckScreenSize'
import ExternalScripts from './ExternalScripts'
import DownloadAppPrompt from './components/DownloadAppPrompt'
import analytics from './stores/analytics'

declare global {
  interface Window {
    cioanalytics?: any
  }
}

configure({
  enforceActions: 'always',
})

export const links: LinksFunction = () => [
  {
    rel: 'preconnect',
    href: 'https://fonts.googleapis.com',
  },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'preload',
    href: '/soloLoaderLogo.png',
    as: 'image',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,100..1000&display=swap',
  },
  {
    rel: 'manifest',
    href: '/manifest.json',
  },
]

export const baseMetaData = [
  {
    name: 'viewport',
    content: 'width=device-width, initial-scale=1, user-scalable=no',
  },
]

export const meta: MetaFunction = () => {
  return [
    ...baseMetaData,
    { title: 'Solo | Your Account' },
  ]
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang='en'>
      <head>
        <meta charSet='utf-8' />
        <Links />
        <Meta />
        <ExternalScripts />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  )
}

export default observer(function App() {
  const store = useContext(RootStore)
  const location = useLocation()
  const navigate = useNavigate()

  useGlobalShortcuts()
  useCheckScreenSize()

  const prevPathRef = useRef(location.pathname)
  useEffect(() => {
    if (prevPathRef.current === location.pathname) return
    prevPathRef.current = location.pathname

    runInAction(() => {
      store.navigationStore.pathname = location.pathname
    })

    setRouteSpan(Sentry.startInactiveSpan({
      name: location.pathname,
      op: 'navigation',
      forceTransaction: true,
    }))
  }, [location.pathname])

  useEffect(() => {
    store.loadStore()
  }, [store])

  useEffect(() => {
    if (store.ready) {
      store.checkAuth(location.pathname, navigate)
      store.uiStore.checkBrowserSupport(location.pathname)
      analytics.listenOnCioEvents(navigate, () => store.searchManifestStore.activeTerms)
    }
    return () => analytics.cleanUpCioEvents()
  }, [store, store.ready, location, navigate, store.authStore.sessionExpired])

  return <>
    <RootStore.Provider value={__store}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <I18nContext.Provider value={__store.i18n}>
          <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            {store.uiStore.shouldRenderUI(location.pathname) && <Outlet />}
          </Box>

          <ActionMenuForUIStore />
          <UiStoreMessageDialog />
          <DropDownMessage />
          <BottomMessageDialog />
          <ImageModal />
          <DevDisplay />
          <RefreshIcon />
          <SearchModal />
          <SetupModal />
          <DownloadAppPrompt />
        </I18nContext.Provider>
      </LocalizationProvider>
    </RootStore.Provider>

    <LoadingSplash />
  </>
})